import { Platform, StyleSheet, ViewStyle } from 'react-native';

import { ContentType } from 'services/data-manager/types';

import { COLOR } from 'utils/colors';
import {
    dimensionsToAspectRatio,
    heightPercentageToDP,
    isTablet,
    isWeb,
    widthPercentageToDP,
} from 'utils/responsiveness';

export enum RibbonTouchableType {
    PORTRAIT = 'PORTRAIT',
    LANDSCAPE = 'LANDSCAPE',
    LANDSCAPE_SMALL = 'LANDSCAPE_SMALL',
    CIRCLE = 'CIRCLE',
}

export const CHANNEL_ITEM_SIZE_WEB = 176;
export const CAST_ITEM_SIZE_WEB = 176;
export const LANDSCAPE_ITEM_SIZE = Platform.select({ web: 272, default: 216 });
export const CAST_MARGIN_WEB = 16;
export const STRIPE_MARGIN_WEB = widthPercentageToDP(16);

const styles = StyleSheet.create({
    overlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: COLOR.BLACK,
    },
    container: {
        overflow: isWeb ? 'visible' : 'hidden',
        // @ts-ignore
        cursor: 'pointer',
    },
    contentContainer: {
        borderRadius: 4,
        overflow: 'hidden',
    },
    stripeContainer: {
        marginRight: Platform.select({
            ios: isTablet ? 16 : 8,
            android: isTablet ? 16 : 8,
            web: STRIPE_MARGIN_WEB,
            default: 8,
        }),
    },
    castContainer: {
        marginRight: Platform.select({
            ios: isTablet ? 16 : 8,
            android: isTablet ? 16 : 8,
            web: CAST_MARGIN_WEB,
            default: 8,
        }),
    },
    nowNextMargins: {
        marginTop: Platform.select({
            web: 0,
            default: isTablet ? 24 : 26,
        }),
        marginBottom: Platform.select({
            web: 40,
            default: 0,
        }),
    },
    channelsMargins: {
        marginTop: Platform.select({
            web: 0,
            default: heightPercentageToDP(40),
        }),
        marginBottom: Platform.select({
            web: 24,
            default: 0,
        }),
    },

    stripeContainerPortrait: {
        width: widthPercentageToDP(104, 132),
    },
    stripeContainerLandscape: {
        width: widthPercentageToDP(LANDSCAPE_ITEM_SIZE, 206, 272),
    },
    stripeContainerLandscapeSmall: {
        width: widthPercentageToDP(104, 132, CHANNEL_ITEM_SIZE_WEB),
    },
    stripeContainerCircle: {
        width: widthPercentageToDP(104, 132),
        borderRadius: widthPercentageToDP(104, 132) / 2,
    },
    collectionContainerPortrait: {
        width: widthPercentageToDP(104, 132),
    },
    collectionContainerLandscape: {
        width: widthPercentageToDP(160, 206, 272),
    },
    collectionContainerLandscapeSmall: {
        width: widthPercentageToDP(104, 132, CHANNEL_ITEM_SIZE_WEB),
    },
    collectionContainerCircle: {
        width: widthPercentageToDP(104, 132),
        borderRadius: widthPercentageToDP(104, 132) / 2,
    },

    stripePortrait: {
        width: widthPercentageToDP(104, 132),
        aspectRatio: dimensionsToAspectRatio(104, 156, 132, 198),
    },
    stripeLandscape: {
        width: widthPercentageToDP(216, 206, 272),
        aspectRatio: 16 / 9,
    },
    stripeLandscapeSmall: {
        width: widthPercentageToDP(104, 132, CHANNEL_ITEM_SIZE_WEB),
        aspectRatio: 16 / 9,
    },
    stripeCircle: {
        width: Platform.select({
            web: CAST_ITEM_SIZE_WEB,
            default: widthPercentageToDP(104, 132),
        }),
        aspectRatio: 1,
    },

    collectionPortrait: {
        width: widthPercentageToDP(104, 132),
        aspectRatio: dimensionsToAspectRatio(104, 156, 132, 198),
    },
    collectionLandscape: {
        width: Platform.select({
            web: widthPercentageToDP(272),
            default: widthPercentageToDP(160, 206),
        }),
        aspectRatio: Platform.select({
            web: dimensionsToAspectRatio(272, 153),
            default: dimensionsToAspectRatio(160, 90, 206, 116),
        }),
    },
    collectionLandscapeSmall: {
        width: Platform.select({
            web: widthPercentageToDP(176),
            default: widthPercentageToDP(104, 132),
        }),
        aspectRatio: Platform.select({
            web: dimensionsToAspectRatio(176, 99),
            default: dimensionsToAspectRatio(104, 59, 132, 74),
        }),
    },
    collectionCircle: {
        width: widthPercentageToDP(104, 132),
        aspectRatio: dimensionsToAspectRatio(104, 132, 104, 132),
    },
});

export const stripeMargins: Partial<
    Record<ContentType, { marginRight: number }>
> = {
    [ContentType.NowNext]: styles.stripeContainer,
    [ContentType.Catchup]: styles.stripeContainer,
    [ContentType.VOD]: styles.stripeContainer,
    [ContentType.Mixed]: styles.stripeContainer,
    [ContentType.DetailsEpisode]: styles.stripeContainer,
    [ContentType.SearchCatchup]: styles.stripeContainer,
    [ContentType.SearchProgram]: styles.stripeContainer,
    [ContentType.SearchVod]: styles.stripeContainer,
    [ContentType.ChannelItem]: styles.stripeContainer,
    [ContentType.CastPerson]: styles.castContainer,
    [ContentType.NPVR]: styles.stripeContainer,
    [ContentType.ContinueWatching]: styles.stripeContainer,
};

export const collectionMargins: Partial<Record<ContentType, ViewStyle>> = {
    [ContentType.NowNext]: styles.nowNextMargins,
    [ContentType.Catchup]: styles.nowNextMargins,
    [ContentType.DetailsEpisode]: styles.nowNextMargins,
    [ContentType.SearchCatchup]: styles.nowNextMargins,
    [ContentType.SearchProgram]: styles.nowNextMargins,
    [ContentType.SearchVod]: styles.nowNextMargins,
    [ContentType.ChannelItem]: styles.channelsMargins,
    [ContentType.NPVR]: styles.nowNextMargins,
    [ContentType.ContinueWatching]: styles.nowNextMargins,
};

export const stripeDimensions: Record<
    RibbonTouchableType,
    { width: number; aspectRatio: number }
> = {
    [RibbonTouchableType.PORTRAIT]: styles.stripePortrait,
    [RibbonTouchableType.LANDSCAPE]: styles.stripeLandscape,
    [RibbonTouchableType.LANDSCAPE_SMALL]: styles.stripeLandscapeSmall,
    [RibbonTouchableType.CIRCLE]: styles.stripeCircle,
};

export const collectionDimensions: Partial<
    Record<RibbonTouchableType, ViewStyle>
> = {
    [RibbonTouchableType.PORTRAIT]: styles.collectionPortrait,
    [RibbonTouchableType.LANDSCAPE]: styles.collectionLandscape,
    [RibbonTouchableType.LANDSCAPE_SMALL]: styles.collectionLandscapeSmall,
    [RibbonTouchableType.CIRCLE]: styles.collectionCircle,
};

export const stripeContainerDimensions: Partial<
    Record<RibbonTouchableType, ViewStyle>
> = {
    [RibbonTouchableType.PORTRAIT]: styles.stripeContainerPortrait,
    [RibbonTouchableType.LANDSCAPE]: styles.stripeContainerLandscape,
    [RibbonTouchableType.LANDSCAPE_SMALL]: styles.stripeContainerLandscapeSmall,
    [RibbonTouchableType.CIRCLE]: styles.stripeContainerCircle,
};

export const collectionContainerDimensions: Partial<
    Record<RibbonTouchableType, ViewStyle>
> = {
    [RibbonTouchableType.PORTRAIT]: styles.collectionContainerPortrait,
    [RibbonTouchableType.LANDSCAPE]: styles.collectionContainerLandscape,
    [RibbonTouchableType.LANDSCAPE_SMALL]:
        styles.collectionContainerLandscapeSmall,
    [RibbonTouchableType.CIRCLE]: styles.collectionContainerCircle,
};

export const metadataTypeToRibbonType: Partial<
    Record<ContentType, RibbonTouchableType>
> = {
    [ContentType.ChannelItem]: RibbonTouchableType.LANDSCAPE_SMALL,
    [ContentType.NowNext]: RibbonTouchableType.LANDSCAPE,
    [ContentType.Catchup]: RibbonTouchableType.LANDSCAPE,
    [ContentType.VOD]: RibbonTouchableType.LANDSCAPE,
    [ContentType.Mixed]: RibbonTouchableType.LANDSCAPE,
    [ContentType.DetailsEpisode]: RibbonTouchableType.LANDSCAPE,
    [ContentType.SearchCatchup]: RibbonTouchableType.LANDSCAPE,
    [ContentType.SearchProgram]: RibbonTouchableType.LANDSCAPE,
    [ContentType.SearchVod]: RibbonTouchableType.LANDSCAPE,
    [ContentType.CastPerson]: RibbonTouchableType.CIRCLE,
    [ContentType.NPVR]: RibbonTouchableType.LANDSCAPE,
    [ContentType.ContinueWatching]: RibbonTouchableType.LANDSCAPE,
};

export default styles;
